import { useRef, useState, useEffect } from 'react';
import {
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { VolumeMax, VolumeX } from '@kula-ai/icons-core';
import { FaPlay, FaPause } from 'react-icons/fa';
import { KText } from '@/ui-library/components';
import { SessionAudioPlayerProps } from '../types';

let currentlyPlayingAudio: HTMLAudioElement | null = null;

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

export const AISessionAudioPlayer: React.FC<SessionAudioPlayerProps> = ({
  recordingUrl,
  durationSeconds,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(durationSeconds);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || durationSeconds);
    };

    const handleTimeUpdate = () => {
      setProgress(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    };
  }, [durationSeconds]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      currentlyPlayingAudio = null;
    } else {
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audio) {
        currentlyPlayingAudio.pause();
      }
      audio.play();
      currentlyPlayingAudio = audio;
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressChange = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value;
    setProgress(value);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const cyclePlaybackSpeed = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const newSpeed = playbackSpeed >= 2 ? 1 : playbackSpeed + 0.25;
    audio.playbackRate = newSpeed;
    setPlaybackSpeed(newSpeed);
  };

  return (
    <Flex
      bg="white"
      borderRadius="48px"
      p="12px 16px"
      alignItems="center"
      gap="12px"
      data-test-id="ai-session-audio-player"
    >
      <audio ref={audioRef} src={recordingUrl} preload="metadata" />

      <Flex
        as="button"
        type="button"
        bg="primary-500"
        w="32px"
        h="32px"
        minW="32px"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        border="none"
        p={0}
        onClick={togglePlayPause}
        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
        _hover={{ bg: 'primary-600' }}
        _focus={{ boxShadow: 'none' }}
        transition="background-color 0.2s"
      >
        <Icon
          as={isPlaying ? FaPause : (FaPlay as any)}
          w="10px"
          h="10px"
          color="base-white"
          ml={isPlaying ? 0 : '2px'}
          aria-hidden
        />
      </Flex>

      <KText size="xs-medium" color="secondary-700" minW="32px">
        {formatTime(progress)}
      </KText>

      <Flex flex={1}>
        <Slider
          aria-label="audio-progress"
          value={progress}
          max={duration}
          onChange={handleProgressChange}
          focusThumbOnChange={false}
        >
          <SliderTrack bg="secondary-100" h="4px" borderRadius="2px">
            <SliderFilledTrack bg="primary-500" />
          </SliderTrack>
          <SliderThumb
            w="10px"
            h="10px"
            border="2px solid"
            borderColor="primary-500"
            bg="base-white"
          />
        </Slider>
      </Flex>

      <KText
        size="xs-medium"
        color="secondary-700"
        minW="32px"
        textAlign="right"
      >
        {formatTime(duration)}
      </KText>

      <Flex
        as="button"
        type="button"
        cursor="pointer"
        onClick={toggleMute}
        p="4px"
        borderRadius="4px"
        border="none"
        bg="transparent"
        alignItems="center"
        justifyContent="center"
        aria-label={isMuted ? 'Unmute recording' : 'Mute recording'}
        _hover={{ bg: 'secondary-50' }}
        _focus={{ boxShadow: 'none' }}
      >
        <Icon
          as={isMuted ? VolumeX : VolumeMax}
          w="16px"
          h="16px"
          color="secondary-500"
          aria-hidden
        />
      </Flex>
      <Flex
        as="button"
        type="button"
        cursor="pointer"
        onClick={cyclePlaybackSpeed}
        p="4px"
        borderRadius="4px"
        border="none"
        bg="transparent"
        minW="28px"
        justifyContent="center"
        alignItems="center"
        aria-label={`Playback speed ${playbackSpeed} times. Press to change speed.`}
        _hover={{ bg: 'secondary-50' }}
        _focus={{ boxShadow: 'none' }}
      >
        <KText size="xs-medium" color="secondary-500" as="span">
          {playbackSpeed}x
        </KText>
      </Flex>
    </Flex>
  );
};
